/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import {signal} from '@preact/signals-react';

type SelectedFacetsState = {
  [tabId: string]: {
    [category: string]: string[];
  };
};

export enum PermanentTabs {
  MY_JOBS = 'my-jobs',
  JOBS = 'jobs',
  WATCHING = 'watching-jobs'
}

/**
 * A record seeding one entry per permanent tab, built from `PermanentTabs`
 * rather than hand-listed.
 *
 * Every per-tab signal needs this shape, both as its initial value and again
 * wherever it is reset, so hand-listing it put the same literal in four places
 * which had to stay in lockstep - and a site missed when a tab is added fails
 * silently, since the state is indexed by string and a missing tab reads
 * `undefined` rather than throwing. Adding a tab is now one edit, to the enum.
 *
 * `forTab` is invoked per tab rather than a value being shared, so tabs never
 * end up holding the same mutable object.
 *
 * @param   forTab Builds the entry for one tab.
 * @returns One entry per member of `PermanentTabs`.
 */
export const initialPerTabState = <T>(forTab: () => T): Record<string, T> => (
  Object.values(PermanentTabs).reduce((state, tab) => ({...state, [tab]: forTab()}), {})
);

/**
 * Global signal (default state) for selected job facets.
 */
export const selectedJobFacetsSignal = signal<SelectedFacetsState>(
  initialPerTabState(() => ({}))
);

/**
 * Sets the entire selectedJobFacetsSignal state.
 * @param newState The new state to set for selectedJobFacetsSignal.
 */
export const setSelectedJobFacetsSignal = (newState: SelectedFacetsState): void => {
  selectedJobFacetsSignal.value = newState;
};
